/* eslint-disable @typescript-eslint/no-unused-vars */
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import Stats from 'three/examples/jsm/libs/stats.module.js';

import { Loader } from "../loaders/assets/loader";
import CameraService from "../services/camera/camera.service";
import PhysicsService from "../services/physics/physics.service";
import AppData from "../app.data";
import { SceneUtils } from "./scene.utils";
import { VRManager } from "./managers/vr.manager";
import { PostProcessingManager } from "./managers/postprocessing.manager";
import { AnimationLoop } from "./services/animation.loop.service";
import type { VRQualityProfile, VRSessionState, VRViewerMedia } from "./managers/vr.types";
import { installXRFrameDetectedPlanesGuard } from "./xr.frame.guard";
import {
  PSEUDO_MOM_BASE_LIGHT_COLOR,
  PSEUDO_MOM_DEFAULT_LIGHT_COLOR,
  PSEUDO_MOM_TAG_LIGHT_HIDDEN_POSITION,
  resolvePseudoMoMWrapperLightColor,
} from "../utils/pseudoMomTagLight.utils";
import {
  applyVRModelPerformanceProfile,
  restoreVRModelPerformanceProfile,
  shouldSimplifyModelForVR,
} from "../utils/vrModelPerformance.utils";
import { cleanupArtworkRuntimeRegistries, getAnimationMixer, getAnimationRoot, isObjectInActiveScene } from "../utils/runtime-registry.utils";
import {
  attachGalleryBenchmarkContextListener,
  markGalleryBenchmark,
  recordGalleryBenchmarkFrame,
} from "../../../performance/galleryBenchmark";
import { resolveRenderQualityCapsForQuality } from "./qualityCaps";
import { updateStrobeLights } from "../utils/strobeLight.utils";

const appData = new AppData();
const FRAMES_TO_RENDER = 3;

/**
 * Main renderer class handling all 3D rendering operations and post-processing effects
 * Manages WebGL rendering, post-processing pipeline, animation loops, and camera controls
 */
class Renderer {
  renderer: THREE.WebGLRenderer;
  canvas!: HTMLElement;

  // Managers
  vrManager: VRManager;
  postProcessingManager: PostProcessingManager;
  sceneUtils: SceneUtils;
  private lastMaterialOverrides: Array<{ meshName: string; channel: string }> = [];
  animationLoop: AnimationLoop;

  // Services
  cameraService: CameraService;
  physicsService: PhysicsService;
  loader: Loader;

  // State
  canvasRect = null as DOMRect | null;
  camera: THREE.PerspectiveCamera;
  debugCamera: THREE.PerspectiveCamera;
  controls: OrbitControls;
  clock = new THREE.Clock();
  stats: any;

  // Settings
  renderQuality: "LQ" | "MQ" | "HQ" = localStorage.getItem('renderQuality') as "LQ" | "MQ" | "HQ" || "MQ";
  renderSettings: any;
  debug: boolean = false;
  showStats: boolean = false;

  // Loop control
  _framesToRender = FRAMES_TO_RENDER;
  renderCounter = setTimeout(() => { }, 1000);
  private animationAccumulator = 0;
  private strobeElapsedSeconds = 0;
  private lastClosestPseudoMoMSlotKey: string | null = null;
  private lastClosestPseudoMoMSlotDistance: number | null = null;
  private litPseudoMoMSlotIndex: number | null = null;
  private pseudoMoMModelRotationPaused = false;
  private static readonly ANIMATION_STEP = 1 / 60;
  private static readonly MAX_ANIMATION_STEPS = 5;
  private static readonly PSEUDO_MOM_MODEL_ROTATION_SPEED = 0.25;
  private vrQualityProfile: VRQualityProfile | null = null;
  private originalPixelRatio = 1;
  private originalShadowMapEnabled: boolean | null = null;
  private originalShadowMapType: THREE.ShadowMapType | null = null;
  private suppressedVRZeroDrawObjects: Array<{ object: THREE.Object3D; visible: boolean }> = [];
  private pendingVRSessionEvent: ((state: VRSessionState) => void) | null = null;
  private pendingVRFailureEvent: ((reason: string) => void) | null = null;
  private benchmarkContextCleanup: (() => void) | null = null;
  constructor(canvas: HTMLCanvasElement, cameraService: CameraService, physicsService: PhysicsService) {
    this.canvas = canvas;
    this.cameraService = cameraService;
    this.physicsService = physicsService;
    this.loader = new Loader();

    // Initialize helpers
    this.sceneUtils = new SceneUtils();

    // Log the Three.js version to the console
    if (typeof THREE !== "undefined" && THREE.REVISION) {
      console.log("Three.js version:", THREE.REVISION);
    } else {
      console.log("Three.js version information not available.");
    }
  }

  // Getters for compatibility
  get composer() { return this.postProcessingManager?.composer; }
  set composer(val) { if (this.postProcessingManager) this.postProcessingManager.composer = val; }
  get renderTarget() { return this.postProcessingManager?.renderTarget; }
  set renderTarget(val) { if (this.postProcessingManager) this.postProcessingManager.renderTarget = val; }
  get outlinePass() { return this.postProcessingManager?.outlinePass; }
  set outlinePass(val) { if (this.postProcessingManager) this.postProcessingManager.outlinePass = val; }
  get bloomPass() { return this.postProcessingManager?.bloomPass; }
  set bloomPass(val) { if (this.postProcessingManager) this.postProcessingManager.bloomPass = val; }
  get lutPass() { return this.postProcessingManager?.lutPass; }
  set lutPass(val) { if (this.postProcessingManager) this.postProcessingManager.lutPass = val; }
  get camSpeed() { return this.cameraService.camSpeed; }
  public initRenderer(localMood?: any) {
    console.log("%cSetting up THREE RENDERER", "color:red");
    if (!this.canvas) { console.error("Error no canvas"); return; }
    this.canvasRect = this.canvas.getBoundingClientRect();

    const moods = appData.spaceData?.data?.moods || [];
    const requestedMoodId = Number.isInteger(appData.galleryData?.data?.mood) ? appData.galleryData.data.mood : 0;
    const safeMood = moods[requestedMoodId] || moods[0] || { renderSettings: {}, debugCameraEnabled: false, materialOverrides: [] };
    const activeMood = localMood || safeMood;
    this.renderSettings = activeMood.renderSettings || {};
    // Debug camera is runtime-only UI state; do not hydrate from persisted mood data on page load.
    this.debug = false;
    (appData as any).debugCameraEnabled = false;
    this.lastMaterialOverrides = Array.isArray(activeMood?.materialOverrides)
      ? activeMood.materialOverrides
        .filter((override: any) => override?.meshName && override?.channel)
        .map((override: any) => ({
          meshName: String(override.meshName),
          channel: String(override.channel)
        }))
      : [];

    this.initWebGLRenderer();
    this.initManagers();
    this.initCameraAndControls();
    this.setComposer();
  }
  private initWebGLRenderer() {
    const isLQ = this.renderQuality === "LQ";
    const isHQ = this.renderQuality === "HQ";
    const qualityCaps = this.getRenderQualityCaps();

    installXRFrameDetectedPlanesGuard();

    this.renderer = new THREE.WebGLRenderer({
      antialias: !isLQ,
      alpha: false,
      stencil: isLQ ? false : true,
      powerPreference: isLQ ? 'low-power' : 'high-performance'
    });

    this.renderer.setPixelRatio(qualityCaps.pixelRatio);
    this.renderer.setSize(this.canvasRect!.width, this.canvasRect!.height);
    this.renderer.autoClear = true;
    this.renderer.xr.enabled = true;
    this.renderer.xr.setReferenceSpaceType('local');
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = isLQ ? THREE.NoToneMapping : this.renderSettings.toneMapping;
    this.renderer.toneMappingExposure = isLQ ? 1 : this.renderSettings.toneMappingExposure;

    this.renderer.shadowMap.enabled = !isLQ;
    if (!isLQ) this.renderer.shadowMap.type = isHQ ? THREE.PCFSoftShadowMap : THREE.PCFShadowMap;

    this.canvas.appendChild(this.renderer.domElement);
    this.benchmarkContextCleanup?.();
    this.benchmarkContextCleanup = attachGalleryBenchmarkContextListener(this.renderer.domElement);
    markGalleryBenchmark("rendererInitializedMs");
    if (this.showStats) this.initStats();
  }

  private initStats() {
    this.stats = new Stats();
    this.stats.showPanel(0);
    this.stats.dom.style.position = "absolute";
    this.stats.dom.style.top = "10px";
    this.stats.dom.style.left = "calc(100% - 90px)";
    this.stats.dom.style.zIndex = "1000";
    this.canvas.appendChild(this.stats.dom);
  }

  private initManagers() {
    this.vrManager = new VRManager(this.renderer, this.canvas, this.physicsService);
    if (this.pendingVRSessionEvent) {
      this.vrManager.setVRSessionEvent(this.pendingVRSessionEvent);
    }
    if (this.pendingVRFailureEvent) {
      this.vrManager.setVRFailureEvent(this.pendingVRFailureEvent);
    }
    this.postProcessingManager = new PostProcessingManager(this.renderer, this.addToRenderQueue.bind(this));
    this.animationLoop = new AnimationLoop(
      this.cameraService, this.physicsService, this.sceneUtils, this.renderer, this.clock,
      this.addToRenderQueue.bind(this), this.render.bind(this)
    );
    this.vrManager.renderer = this.renderer;
    this.postProcessingManager.renderer = this.renderer;
    this.vrManager.checkAndCreateVRButton();
    appData.renderService = this;
    this.postProcessingManager.init(this.canvasRect!, this.renderQuality === "LQ");
    if (this.stats) this.vrManager.setStats(this.stats);

    this.vrManager.onEnterVRCallback = () => {
      this.applyVRQualityProfile("vr-safe");
      this.addToRenderQueue(10);
    };
    this.vrManager.onExitVRCallback = () => {
      this.clearVRQualityProfile();
      this.setComposer();
      this.addToRenderQueue(10);
    };
  }
  private initCameraAndControls() {
    this.camera = this.cameraService.getCamera();
    appData.debugCamera = new THREE.PerspectiveCamera(75, this.canvasRect!.width / this.canvasRect!.height, 0.1, 1000);
    appData.debugCamera.position.set(0, 5, 25);

    this.controls = new OrbitControls(appData.debugCamera, this.renderer.domElement);
    this.controls.autoRotate = false;
    this.controls.enableDamping = false;
    this.controls.enabled = !this.renderer.xr.isPresenting;
    this.controls.addEventListener("change", () => { });
    this.vrManager.setControls(this.controls);
  }

  public startAnimationLoop() {
    this.animationLoop.start();
  }

  public addToRenderQueue(frames: number = FRAMES_TO_RENDER) {
    this._framesToRender = frames;
  }

  public setPseudoMoMModelRotationPaused(paused: boolean) {
    this.pseudoMoMModelRotationPaused = paused;
  }

  public hasQueuedFrames() {
    return this._framesToRender > 0;
  }

  public hasVisibleActiveAnimation() {
    return this.sceneUtils.skinnedMeshesAreInFrustum(this.physicsService);
  }

  public hasActiveVisibleVideo() {
    return this.sceneUtils.videoIsInFrustum();
  }

  public hasActiveContinuousWork() {
    return this.renderer?.xr?.isPresenting ||
      appData.viewerRotationActive ||
      this.hasActiveVisibleVideo() ||
      this.hasVisibleActiveAnimation() ||
      this.hasActiveStrobeAnimation() ||
      this.shouldAnimatePseudoMoMModelRotation();
  }

  private render(delta: number) {
    this.vrManager?.update(delta);

    const shouldUpdateAnimations = this.hasVisibleActiveAnimation();
    const shouldAnimatePseudoMoMModelRotation = this.shouldAnimatePseudoMoMModelRotation();
    this.strobeElapsedSeconds += Math.max(0, delta);
    const strobeChanged = updateStrobeLights(
      appData.strobeLights,
      this.strobeElapsedSeconds,
      appData.flashingEffectsEnabled
    );
    let shouldRender = this._framesToRender > 0 ||
      this.renderer?.xr?.isPresenting ||
      appData.viewerRotationActive ||
      this.hasActiveVisibleVideo() ||
      shouldUpdateAnimations ||
      strobeChanged ||
      shouldAnimatePseudoMoMModelRotation;

    if (shouldRender) {
      let camera = this.debug ? appData.debugCamera : this.camera;
      const hasRenderableContext = Boolean(camera && (camera as any).isCamera && this.renderer && appData.spaceScene);
      if (!hasRenderableContext) {
        // Avoid leaving stale queued frames when scene/camera is transiently unavailable.
        if (this._framesToRender > 0) this._framesToRender = 0;
        return;
      }

      if (this.renderer.xr.isPresenting) {
        this.suppressZeroDrawRenderablesForVR();
        this.renderer.render(appData.spaceScene, camera);
      } else {
        this.postProcessingManager.render();
      }

      recordGalleryBenchmarkFrame(delta * 1000);
      if (this.showStats) this.stats.update();
      if (this._framesToRender > 0) {
        this._framesToRender--;
      }

      if (shouldUpdateAnimations) {
        this.animationAccumulator += Math.min(delta, Renderer.ANIMATION_STEP * Renderer.MAX_ANIMATION_STEPS);
        let steps = 0;

        while (this.animationAccumulator >= Renderer.ANIMATION_STEP && steps < Renderer.MAX_ANIMATION_STEPS) {
          appData.skinnedMeshes.forEach((entry: any) => {
            const root = getAnimationRoot(entry);
            const mixer = getAnimationMixer(entry);
            if (mixer && root && isObjectInActiveScene(root)) {
              mixer.update(Renderer.ANIMATION_STEP);
            }
          });
          this.animationAccumulator -= Renderer.ANIMATION_STEP;
          steps++;
        }
      }

      if (appData.spaceData?.subtype === "pseudoMoM") {
        this.updatePseudoMoMLights();
        this.updateLitPseudoMoMModelRotation(delta);
      }
    }
  }

  private hasActiveStrobeAnimation() {
    return appData.strobeLights.length > 0 && appData.flashingEffectsEnabled;
  }

  private shouldAnimatePseudoMoMModelRotation() {
    return Boolean(this.getLitPseudoMoMModelRotationTarget());
  }

  private getLitPseudoMoMModelRotationTarget() {
    if (this.pseudoMoMModelRotationPaused) return null;
    if (appData.viewerRotationActive) return null;
    if (appData.galleryData?.data?.pseudoMomAutoRotateObjects !== true) return null;
    if (this.litPseudoMoMSlotIndex === null || !appData.artworksScene) return null;
    const wrapper = appData.galleryData?.artworkWrappers?.find((candidate: any) => {
      return Number(candidate?.slotTarget) === Number(this.litPseudoMoMSlotIndex);
    });
    const activeArtwork = wrapper?.artworks?.[Number(wrapper?.activeArtwork)];
    if (activeArtwork?.type !== "3dmodel") return null;

    const slotObject = appData.artworksScene.children?.find((child: any) => {
      return Number(child?.userData?.index) === Number(this.litPseudoMoMSlotIndex);
    });
    if (!slotObject?.visible) return null;

    let target: any = null;
    slotObject.traverse?.((child: any) => {
      if (!target && child?.userData?.pseudoMoMAutoRotate3dModel) {
        target = child;
      }
    });
    return target;
  }

  private updateLitPseudoMoMModelRotation(delta: number) {
    const target = this.getLitPseudoMoMModelRotationTarget();
    if (!target) return;

    const baseQuaternion = this.getPseudoMoMBaseQuaternion(target);
    const nextOffset = (Number(target.userData.pseudoMoMAutoRotationOffset) || 0) +
      delta * Renderer.PSEUDO_MOM_MODEL_ROTATION_SPEED;
    target.userData.pseudoMoMAutoRotationOffset = nextOffset;
    this.applyPseudoMoMHorizontalRotation(target, baseQuaternion, nextOffset);
  }

  private getPseudoMoMBaseQuaternion(target: any) {
    const existingBase = target.userData.pseudoMoMBaseQuaternion;
    const lastAutoQuaternion = target.userData.pseudoMoMLastAutoQuaternion;
    const hasManualRotation =
      lastAutoQuaternion?.isQuaternion &&
      target.quaternion?.angleTo?.(lastAutoQuaternion) > 0.000001;

    if (existingBase?.isQuaternion && !hasManualRotation) {
      return existingBase;
    }

    const baseQuaternion = target.quaternion.clone();
    target.userData.pseudoMoMBaseQuaternion = baseQuaternion;
    target.userData.pseudoMoMAutoRotationOffset = 0;
    target.userData.pseudoMoMBaseRotationY = target.rotation.y;
    return baseQuaternion;
  }

  private applyPseudoMoMHorizontalRotation(target: any, baseQuaternion: THREE.Quaternion, offset: number) {
    const parentWorldQuaternion = target.parent?.getWorldQuaternion?.(new THREE.Quaternion());
    const horizontalAxis = new THREE.Vector3(0, 1, 0);
    if (parentWorldQuaternion?.isQuaternion) {
      horizontalAxis.applyQuaternion(parentWorldQuaternion.invert()).normalize();
    }

    const horizontalYaw = new THREE.Quaternion().setFromAxisAngle(horizontalAxis, offset);
    target.quaternion.copy(baseQuaternion).premultiply(horizontalYaw);
    target.userData.pseudoMoMLastAutoQuaternion = target.quaternion.clone();
  }

  private updatePseudoMoMLights() {
    const MAX_CHECK_DISTANCE = 7;
    const lights = appData.spaceData.pseudoMoMLights;
    const slots = appData.spaceData?.data?.slots;
    const activeCamera = this.debug ? appData.debugCamera : this.camera;
    const candidateSlots = slots ?? [];

    if (!lights?.length || !candidateSlots.length || !activeCamera) return;

    const cameraPosition = new THREE.Vector3();
    const cameraDirection = new THREE.Vector3();
    activeCamera.getWorldPosition(cameraPosition);
    activeCamera.getWorldDirection(cameraDirection).normalize();

    const slotCandidates = candidateSlots
      .map((slot: any) => {
        const slotPosition = this.getPseudoMoMSlotWorldPosition(slot);
        const toSlot = slotPosition.clone().sub(cameraPosition);
        const distance = toSlot.length();
        const inFront = distance === 0 ? true : toSlot.normalize().dot(cameraDirection) > 0;
        const slotKey = typeof slot.index === "number"
          ? `slot-${slot.index}`
          : `${slot.transforms.position.x}|${slot.transforms.position.y}|${slot.transforms.position.z}`;
        return { slot, slotPosition, distance, inFront, slotKey };
      });

    const closestSlot = slotCandidates
      .filter((slot: any) => slot.distance < MAX_CHECK_DISTANCE)
      .sort((a, b) => {
        if (a.inFront !== b.inFront) return a.inFront ? -1 : 1;
        return a.distance - b.distance;
      })[0];

    if (!closestSlot) {
      if (this.shouldOptimizePseudoMoMLightsForVR()) {
        this.hidePseudoMoMLights(lights);
      }
      if (this.lastClosestPseudoMoMSlotKey === null) return;
      this.lastClosestPseudoMoMSlotKey = null;
      this.lastClosestPseudoMoMSlotDistance = null;
      this.litPseudoMoMSlotIndex = null;
      this.hidePseudoMoMLights(lights);
      return;
    }

    const previousSlot = slotCandidates.find(
      (slot: any) => slot.slotKey === this.lastClosestPseudoMoMSlotKey
    );
    const previousSlotDistance = previousSlot?.distance ?? Number.POSITIVE_INFINITY;
    const hasNewCloserSlot =
      this.lastClosestPseudoMoMSlotKey === null ||
      (closestSlot.slotKey !== this.lastClosestPseudoMoMSlotKey &&
        closestSlot.distance < previousSlotDistance);

    if (!hasNewCloserSlot) return;

    this.lastClosestPseudoMoMSlotKey = closestSlot.slotKey;
    this.lastClosestPseudoMoMSlotDistance = closestSlot.distance;

    const randomLight = lights[Math.floor(Math.random() * lights.length)];
    this.applyPseudoMoMLightToSlot(closestSlot.slot, randomLight);
  }

  private getPseudoMoMSlotWorldPosition(slot: any) {
    const slotPosition = new THREE.Vector3(
      slot.transforms.position.x,
      slot.transforms.position.y,
      slot.transforms.position.z
    );

    if (!this.renderer?.xr?.isPresenting || !appData.artworksScene) {
      return slotPosition;
    }

    appData.artworksScene.updateMatrixWorld?.(true);
    return appData.artworksScene.localToWorld(slotPosition);
  }

  public refreshPseudoMoMLightForSlot(slotTarget: number) {
    if (appData.spaceData?.subtype !== "pseudoMoM") return;

    const slot = appData.spaceData?.data?.slots?.find((candidate: any) => {
      return Number(candidate?.index) === Number(slotTarget);
    });
    if (!slot) return;

    this.lastClosestPseudoMoMSlotKey = this.getPseudoMoMSlotKey(slot);
    this.lastClosestPseudoMoMSlotDistance = null;
    this.applyPseudoMoMLightToSlot(slot, this.getActivePseudoMoMLight(appData.spaceData?.pseudoMoMLights));
  }

  private applyPseudoMoMLightToSlot(slot: any, light: any) {
    const lights = appData.spaceData?.pseudoMoMLights;
    if (!lights?.length || !light) return;

    const artworkWrapper = appData.galleryData?.artworkWrappers?.find((wrapper: any) => {
      return Number(wrapper?.slotTarget) === Number(slot?.index);
    });
    const color = resolvePseudoMoMWrapperLightColor(artworkWrapper);

    this.setPseudoMoMLightColor(light, color ?? PSEUDO_MOM_DEFAULT_LIGHT_COLOR);
    this.setPseudoMoMLightActive(light);
    light.position.set(
      slot.transforms.position.x,
      slot.transforms.position.y + 2,
      slot.transforms.position.z
    );

    this.litPseudoMoMSlotIndex = Number.isFinite(Number(slot?.index)) ? Number(slot.index) : null;

    lights.forEach((candidate: any) => {
      if (candidate === light) return;
      this.setPseudoMoMLightInactive(candidate);
      candidate.position.copy(PSEUDO_MOM_TAG_LIGHT_HIDDEN_POSITION);
    });
  }

  private setPseudoMoMLightColor(light: any, color: number) {
    const baseLightColor = Number.isFinite(Number(light?.userData?.pseudoMoMBaseColor))
      ? Number(light.userData.pseudoMoMBaseColor)
      : PSEUDO_MOM_BASE_LIGHT_COLOR;
    light.color?.setHex?.(baseLightColor);
    light.children?.forEach((child: any) => {
      const materials = Array.isArray(child.material) ? child.material : [child.material];
      materials.forEach((material: any) => material?.color?.setHex?.(color));
    });
  }

  private getActivePseudoMoMLight(lights: any[] | undefined) {
    if (!lights?.length) return null;

    return lights.find((light: any) => !light.position.equals(PSEUDO_MOM_TAG_LIGHT_HIDDEN_POSITION)) || lights[0];
  }

  private hidePseudoMoMLights(lights: any[]) {
    this.litPseudoMoMSlotIndex = null;
    lights.forEach((light: any) => {
      this.setPseudoMoMLightInactive(light);
      light.position.copy(PSEUDO_MOM_TAG_LIGHT_HIDDEN_POSITION);
    });
  }

  private shouldOptimizePseudoMoMLightsForVR() {
    return Boolean(this.renderer?.xr?.isPresenting);
  }

  private getPseudoMoMLightBaseIntensity(light: any) {
    const storedBaseIntensity = Number(light?.userData?.pseudoMoMBaseIntensity);
    if (Number.isFinite(storedBaseIntensity)) return storedBaseIntensity;

    const currentIntensity = Number(light?.intensity);
    const baseIntensity = Number.isFinite(currentIntensity) && currentIntensity > 0
      ? currentIntensity
      : 4;
    light.userData = light.userData || {};
    light.userData.pseudoMoMBaseIntensity = baseIntensity;
    return baseIntensity;
  }

  private setPseudoMoMLightActive(light: any) {
    if (!this.shouldOptimizePseudoMoMLightsForVR()) return;
    this.rememberPseudoMoMLightBase(light);
    light.visible = true;
    light.intensity = this.vrQualityProfile === "vr-degraded"
      ? Math.min(this.getPseudoMoMLightBaseIntensity(light), 2.5)
      : this.getPseudoMoMLightBaseIntensity(light);
    const baseDistance = this.getPseudoMoMLightBaseDistance(light);
    light.distance = this.vrQualityProfile === "vr-degraded"
      ? (baseDistance > 0 ? Math.min(baseDistance, 12) : 12)
      : baseDistance;
    light.decay = this.vrQualityProfile === "vr-degraded"
      ? 2
      : this.getPseudoMoMLightBaseDecay(light);
  }

  private setPseudoMoMLightInactive(light: any) {
    if (!this.shouldOptimizePseudoMoMLightsForVR()) return;
    this.rememberPseudoMoMLightBase(light);
    light.intensity = 0;
    light.visible = false;
  }

  private restorePseudoMoMLightIntensities() {
    appData.spaceData?.pseudoMoMLights?.forEach((light: any) => {
      const baseIntensity = Number(light?.userData?.pseudoMoMBaseIntensity);
      if (Number.isFinite(baseIntensity)) {
        light.intensity = baseIntensity;
      }
      const baseDistance = Number(light?.userData?.pseudoMoMBaseDistance);
      if (Number.isFinite(baseDistance)) {
        light.distance = baseDistance;
      }
      const baseDecay = Number(light?.userData?.pseudoMoMBaseDecay);
      if (Number.isFinite(baseDecay)) {
        light.decay = baseDecay;
      }
      const baseColor = Number(light?.userData?.pseudoMoMBaseColor);
      light.color?.setHex?.(Number.isFinite(baseColor) ? baseColor : PSEUDO_MOM_BASE_LIGHT_COLOR);
      if (typeof light?.userData?.pseudoMoMBaseVisible === "boolean") {
        light.visible = light.userData.pseudoMoMBaseVisible;
      }
    });
  }

  private rememberPseudoMoMLightBase(light: any) {
    if (!light) return;
    light.userData = light.userData || {};
    this.getPseudoMoMLightBaseIntensity(light);
    this.getPseudoMoMLightBaseDistance(light);
    this.getPseudoMoMLightBaseDecay(light);
    if (typeof light.userData.pseudoMoMBaseVisible !== "boolean") {
      light.userData.pseudoMoMBaseVisible = light.visible !== false;
    }
  }

  private getPseudoMoMLightBaseDistance(light: any) {
    const storedBaseDistance = Number(light?.userData?.pseudoMoMBaseDistance);
    if (Number.isFinite(storedBaseDistance)) return storedBaseDistance;

    const currentDistance = Number(light?.distance);
    const baseDistance = Number.isFinite(currentDistance) && currentDistance > 0
      ? currentDistance
      : 0;
    light.userData = light.userData || {};
    light.userData.pseudoMoMBaseDistance = baseDistance;
    return baseDistance;
  }

  private getPseudoMoMLightBaseDecay(light: any) {
    const storedBaseDecay = Number(light?.userData?.pseudoMoMBaseDecay);
    if (Number.isFinite(storedBaseDecay)) return storedBaseDecay;

    const currentDecay = Number(light?.decay);
    const baseDecay = Number.isFinite(currentDecay) ? currentDecay : 2;
    light.userData = light.userData || {};
    light.userData.pseudoMoMBaseDecay = baseDecay;
    return baseDecay;
  }

  private getPseudoMoMSlotKey(slot: any) {
    return typeof slot.index === "number"
      ? `slot-${slot.index}`
      : `${slot.transforms.position.x}|${slot.transforms.position.y}|${slot.transforms.position.z}`;
  }

  public killRenderer() {
    this.animationLoop?.stop();
    if (this.renderCounter) clearTimeout(this.renderCounter);
    cleanupArtworkRuntimeRegistries();

    if (appData.spaceScene) {
      appData.spaceScene.traverse((child) => {
        if (child.userData && child.userData.loop) {
          clearInterval(child.userData.loop);
          child.userData.loop = null;
        }
      });
    }

    this.vrManager?.kill();
    this.clearVRQualityProfile();
    this.postProcessingManager?.dispose();
    if (appData.spaceScene?.environment) {
      appData.spaceScene.environment.dispose();
      appData.spaceScene.environment = null;
    }
    if (appData.artworksScene?.environment) {
      appData.artworksScene.environment.dispose();
      appData.artworksScene.environment = null;
    }

    const skyMesh = appData?.spaceScene?.getObjectByName("sky");
    if (skyMesh && skyMesh instanceof THREE.Mesh && skyMesh.material.map) {
      skyMesh.material.map.dispose();
      skyMesh.material.map = null;
      skyMesh.material.needsUpdate = true;
    }

    this.renderer?.dispose();
    // Aggressively release GPU resources; context loss is heavy-handed but frees VRAM between spaces.
    try {
      this.renderer?.forceContextLoss();
    } catch (err) {
      console.log("forceContextLoss failed", err);
    }
    this.benchmarkContextCleanup?.();
    this.benchmarkContextCleanup = null;
    // @ts-ignore
    this.renderer = null;
    this.canvas.innerHTML = "";
  }

  // Delegated Methods
  public setComposer() {
    if (!this.postProcessingManager) return;
    if (!this.camera || !(this.camera as any).isCamera) return;
    if (!appData.spaceScene) return;
    this.postProcessingManager.setComposer(this.renderSettings, this.camera, this.renderQuality, this.debug);
  }
  public setOutlinedObject(object: THREE.Object3D | null) { this.postProcessingManager?.setOutlinedObject(object); }
  public updateLUT(lut: any) { this.postProcessingManager.updateLUT(lut); }
  public updateDotScreen(postProcessing: any) { this.postProcessingManager.updateDotScreen(postProcessing); }
  public updateNoise(postProcessing: any) { this.postProcessingManager.updateNoise(postProcessing); }
  public updateArtworkMapIntensity(mapIntensity: number) { this.applyArtworkMapIntensity(mapIntensity); }
  public updateHDR(hdri: any) { return this.postProcessingManager.updateHDR(hdri); }
  public updateSkyImage(skyImage: any, skyModelName: string) { return this.postProcessingManager.updateSkyImage(skyImage, skyModelName); }
  public updateVRCursor(mouseX: number, mouseY: number) { this.vrManager?.updateVRCursor(mouseX, mouseY); }
  public updateMaterialOverrides(data) { this.sceneUtils.updateMaterialOverrides(data); }
  public isVRPresenting() { return this.vrManager?.isVRPresenting() || false; }
  public getVRSessionState(): VRSessionState | null { return this.vrManager?.getVRSessionState() || null; }
  public endVRSession() { return this.vrManager?.endVRSession() || Promise.resolve(); }
  public openVRViewer(media: VRViewerMedia) { return this.vrManager?.openViewer(media) || Promise.resolve(); }
  public closeVRViewer() { this.vrManager?.closeViewer(); }
  public setVRSessionEvent(method: (state: VRSessionState) => void) {
    this.pendingVRSessionEvent = method;
    this.vrManager?.setVRSessionEvent(method);
  }
  public setVRFailureEvent(method: (reason: string) => void) {
    this.pendingVRFailureEvent = method;
    this.vrManager?.setVRFailureEvent(method);
  }
  public getRenderQualityCaps() {
    return resolveRenderQualityCapsForQuality(this.renderQuality);
  }
  private skinnedMeshesAreInFrustum() { return this.sceneUtils.skinnedMeshesAreInFrustum(this.physicsService); }
  private videoIsInFrustum() { return this.sceneUtils.videoIsInFrustum(); }

  public applyVRQualityProfile(profile: VRQualityProfile) {
    if (!this.renderer || this.vrQualityProfile === profile) {
      return;
    }

    if (this.vrQualityProfile === null) {
      this.originalPixelRatio = this.renderer.getPixelRatio();
      if (this.renderer.shadowMap) {
        this.originalShadowMapEnabled = this.renderer.shadowMap.enabled;
        this.originalShadowMapType = this.renderer.shadowMap.type;
      }
    }

    this.vrQualityProfile = profile;
    this.applyVRSceneQualityProfile(profile);
    if (this.renderer.xr?.isPresenting) {
      return;
    }

    const pixelRatio = profile === "vr-degraded"
      ? Math.min(0.75, this.getRenderQualityCaps().pixelRatio)
      : Math.min(1, this.getRenderQualityCaps().pixelRatio);
    this.renderer.setPixelRatio(pixelRatio);
    this.renderer.setSize(this.canvasRect!.width, this.canvasRect!.height);
  }

  public clearVRQualityProfile() {
    this.restorePseudoMoMLightIntensities();
    this.restoreVRSceneQualityProfile();
    this.restoreVRArtworkScenePerformanceProfile();
    this.restoreVRZeroDrawRenderables();

    if (!this.renderer || this.vrQualityProfile === null) {
      return;
    }

    if (!this.renderer.xr?.isPresenting) {
      this.renderer.setPixelRatio(this.originalPixelRatio || 1);
      this.renderer.setSize(this.canvasRect!.width, this.canvasRect!.height);
    }
    this.vrQualityProfile = null;
  }

  private applyVRSceneQualityProfile(profile: VRQualityProfile) {
    if (!this.renderer?.xr?.isPresenting) {
      return;
    }

    if (this.renderer.shadowMap) {
      this.renderer.shadowMap.enabled = false;
    }
    this.applyVRArtworkScenePerformanceProfile(profile);

    if (profile === "vr-degraded") {
      const activeLight = this.getActivePseudoMoMLight(appData.spaceData?.pseudoMoMLights);
      if (activeLight) {
        this.setPseudoMoMLightActive(activeLight);
      }
      appData.spaceData?.pseudoMoMLights?.forEach((light: any) => {
        if (light !== activeLight) {
          this.setPseudoMoMLightInactive(light);
        }
      });
    }
  }

  public prepareObjectForVR(object: THREE.Object3D) {
    if (!this.renderer?.xr?.isPresenting || !object) {
      return;
    }

    applyVRModelPerformanceProfile(object, {
      simplifyMaterials: this.vrQualityProfile === "vr-degraded" || shouldSimplifyModelForVR(object),
    });
  }

  private applyVRArtworkScenePerformanceProfile(profile: VRQualityProfile) {
    const artworkRoots = appData.artworksScene?.children || [];
    artworkRoots.forEach((root: THREE.Object3D) => {
      applyVRModelPerformanceProfile(root, {
        simplifyMaterials: profile === "vr-degraded" || shouldSimplifyModelForVR(root),
      });
    });
  }

  private restoreVRArtworkScenePerformanceProfile() {
    if (!appData.artworksScene) {
      return;
    }

    restoreVRModelPerformanceProfile(appData.artworksScene);
  }

  private restoreVRSceneQualityProfile() {
    if (!this.renderer?.shadowMap || typeof this.originalShadowMapEnabled !== "boolean") {
      return;
    }

    this.renderer.shadowMap.enabled = this.originalShadowMapEnabled;
    if (this.originalShadowMapType !== null) {
      this.renderer.shadowMap.type = this.originalShadowMapType;
    }
    this.originalShadowMapEnabled = null;
    this.originalShadowMapType = null;
  }

  private suppressZeroDrawRenderablesForVR() {
    if (!this.renderer?.xr?.isPresenting || !appData.spaceScene) {
      return;
    }
    this.suppressedVRZeroDrawObjects = this.suppressedVRZeroDrawObjects || [];

    appData.spaceScene.traverse((object: any) => {
      if (!object?.visible || !this.isZeroDrawRenderable(object)) {
        return;
      }

      this.suppressedVRZeroDrawObjects.push({ object, visible: object.visible });
      object.visible = false;
    });
  }

  private restoreVRZeroDrawRenderables() {
    this.suppressedVRZeroDrawObjects = this.suppressedVRZeroDrawObjects || [];
    this.suppressedVRZeroDrawObjects.forEach(({ object, visible }) => {
      object.visible = visible;
    });
    this.suppressedVRZeroDrawObjects = [];
  }

  private isZeroDrawRenderable(object: any) {
    if (!(object?.isMesh || object?.isLine || object?.isLineSegments || object?.isPoints)) {
      return false;
    }

    if (object?.isInstancedMesh && Number(object.count) <= 0) {
      return true;
    }

    const geometry = object.geometry as THREE.BufferGeometry | undefined;
    if (!geometry?.isBufferGeometry) {
      return false;
    }

    if (geometry.drawRange?.count === 0) {
      return true;
    }

    const positionCount = geometry.getAttribute?.("position")?.count;
    const indexCount = geometry.getIndex?.()?.count;
    const drawableCount = Number.isFinite(indexCount)
      ? Number(indexCount)
      : Number(positionCount);
    return !Number.isFinite(drawableCount) || drawableCount <= 0;
  }

  private applyMapIntensityToMaterial(material: any, mapIntensity: number): boolean {
    if (!material) return false;
    const userData = material.userData ?? (material.userData = {});
    const hasMapIntensityUniform = Object.prototype.hasOwnProperty.call(userData, "mapIntensity");

    if (hasMapIntensityUniform) {
      if (typeof userData.mapIntensity === "object" && userData.mapIntensity !== null && "value" in userData.mapIntensity) {
        userData.mapIntensity.value = mapIntensity;
      } else {
        userData.mapIntensity = mapIntensity;
      }
      material.needsUpdate = true;
      return true;
    }

    // Fallback for material variants that do not use custom mapIntensity uniform.
    if (!material.map) return false;

    if (typeof material.emissiveIntensity === "number" && material.emissiveMap) {
      if (typeof userData.__baseEmissiveIntensity !== "number") {
        userData.__baseEmissiveIntensity = material.emissiveIntensity;
      }
      material.emissiveIntensity = userData.__baseEmissiveIntensity * mapIntensity;
      material.needsUpdate = true;
      return true;
    }

    if (material.color?.isColor) {
      if (!userData.__baseColor && material.color?.clone) {
        userData.__baseColor = material.color.clone();
      }
      if (userData.__baseColor?.isColor && material.color?.copy) {
        material.color.copy(userData.__baseColor).multiplyScalar(mapIntensity);
        material.needsUpdate = true;
        return true;
      }
    }

    return false;
  }

  private applyArtworkMapIntensity(rawMapIntensity: any) {
    const mapIntensity = Number(rawMapIntensity);
    if (!Number.isFinite(mapIntensity)) return;

    let didUpdate = false;
    appData.artworksScene?.traverse((child: any) => {
      if (!child?.isMesh || !child.material) return;
      const materials = Array.isArray(child.material) ? child.material : [child.material];
      materials.forEach((material: any) => {
        didUpdate = this.applyMapIntensityToMaterial(material, mapIntensity) || didUpdate;
      });
    });

    if (didUpdate) {
      this.addToRenderQueue();
    }
  }

  /**
   * Sync renderer with mood settings.
   * Called when mood changes to ensure renderer state matches mood data.
   * This is the reactive approach - renderer syncs itself when given mood data.
   */
  public syncWithMood(moodData: any) {
    if (!moodData) return;
    if (!this.renderer || !this.postProcessingManager) return;

    // Debug camera is runtime-only and controlled by modal toggle state.
    const nextDebug = (appData as any).debugCameraEnabled === true;
    const debugChanged = nextDebug !== this.debug;
    this.debug = nextDebug;
    (appData as any).debugCameraEnabled = this.debug;

    const renderSettings = moodData.renderSettings;
    if (renderSettings) {
      const currentPostProcessing = this.renderSettings?.postProcessing;
      const nextPostProcessing = renderSettings.postProcessing;
      const currentStructure = Array.isArray(currentPostProcessing)
        ? currentPostProcessing.map((pass: any) => pass?.type || "").join("|")
        : "";
      const nextStructure = Array.isArray(nextPostProcessing)
        ? nextPostProcessing.map((pass: any) => pass?.type || "").join("|")
        : "";
      const postProcessingStructureChanged = currentStructure !== nextStructure;

      const currentLutCubeMap = Array.isArray(currentPostProcessing)
        ? (currentPostProcessing.find((pass: any) => pass?.type === "lut") as any)?.cubeMapUrl || ""
        : "";
      const nextLutSettings = Array.isArray(nextPostProcessing)
        ? nextPostProcessing.find((pass: any) => pass?.type === "lut")
        : null;
      const nextLutCubeMap = nextLutSettings?.cubeMapUrl || "";
      const lutCubeMapChanged = currentLutCubeMap !== nextLutCubeMap;

      this.renderSettings = renderSettings;

      // Sync tone mapping settings
      if (this.renderQuality !== "LQ") {
        if (renderSettings.toneMapping !== undefined) {
          this.renderer.toneMapping = renderSettings.toneMapping;
        }
        if (renderSettings.toneMappingExposure !== undefined) {
          this.renderer.toneMappingExposure = renderSettings.toneMappingExposure;
        }
      }

      const shouldRebuildComposer = (debugChanged || postProcessingStructureChanged) && !this.renderer.xr.isPresenting;
      if (shouldRebuildComposer) {
        this.setComposer();
      } else if (renderSettings.postProcessing && this.postProcessingManager) {
        this.postProcessingManager.syncWithMood(renderSettings.postProcessing);
      }

      if (nextLutSettings && lutCubeMapChanged) {
        this.updateLUT(nextLutSettings);
      }

      if (renderSettings.artworkMapIntensity !== undefined) {
        this.applyArtworkMapIntensity(renderSettings.artworkMapIntensity);
      }
    }

    // Sync material overrides, clearing stale ones from previous mood first.
    const nextOverrides = Array.isArray(moodData.materialOverrides)
      ? moodData.materialOverrides.filter((override: any) => override?.meshName && override?.channel)
      : moodData.materialOverrides && moodData.materialOverrides.meshName && moodData.materialOverrides.channel
        ? [moodData.materialOverrides]
        : [];
    const nextOverrideKeys = new Set(nextOverrides.map((override: any) => `${override.meshName}|${override.channel}`));

    for (const previous of this.lastMaterialOverrides) {
      const previousKey = `${previous.meshName}|${previous.channel}`;
      if (!nextOverrideKeys.has(previousKey)) {
        this.updateMaterialOverrides({
          meshName: previous.meshName,
          channel: previous.channel,
          textureUrl: "",
          repeat: 1
        });
      }
    }

    nextOverrides.forEach((override: any) => this.updateMaterialOverrides(override));
    this.lastMaterialOverrides = nextOverrides.map((override: any) => ({
      meshName: String(override.meshName),
      channel: String(override.channel)
    }));

    // Queue a render to apply changes
    this.addToRenderQueue(debugChanged ? FRAMES_TO_RENDER * 10 : FRAMES_TO_RENDER);
  }
}

export default Renderer;
